/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import React, { Fragment } from 'react';
import { ScopeQuestionOptionalValue } from '../../../types/surveyScope';
import { ColumnCommonProps, QuestionListColumn, SurveyQuestionListProps } from '../survey-question-list';
import { ColumnSelectbox } from './ColumnSelectbox';
import { LazyLoadingTrackingList } from './LazyLoadingTrackingList';
import { Divider, Row } from '../../tracking-list';
import { RowEmpty } from './RowEmpty';
import { BulkActionToolbarSelectedUtrv } from './BulkActionToolbar';
import { ColumnTitle } from './ColumnTitle';
import { ColumnValue } from './ColumnValue';
import { ColumnStatusIcon } from './ColumnStatusIcon';
import { generateUrl } from '../../../routes/util';
import { ROUTES } from '../../../constants/routes';
import { useHistory, useLocation } from 'react-router-dom';
import { HighlightQuestion } from '../utils';
import { ColumnBookmark } from './ColumnBookmark';

const defaultColumns: QuestionListColumn[] = [
  ColumnBookmark,
  ColumnTitle,
  Divider,
  ColumnValue,
  Divider,
  ColumnStatusIcon,
]

interface QuestionListProps extends Pick<SurveyQuestionListProps,
  'columns' |
  'goToQuestion' |
  'initiativeId' |
  'surveyId'
> {
  questionGroupId: string,
  questionList: ScopeQuestionOptionalValue[],
  startIndex: number;
  toggleSelectQuestion: (utrv: BulkActionToolbarSelectedUtrv) => void;
  enableBulkActions: boolean;
  colour?: string;
  // @deprecated in favour of preferredAltCodes
  alternativeCode?: string;
  preferredAltCodes?: string[];
  highlightQuestion?: HighlightQuestion;
  isQuestionSelected: (utrvId: string) => boolean;
  rowRef?: React.ForwardedRef<HTMLDivElement>;
  isBookmarked: (utrvId: string) => boolean,
  toggleBookmark: (utrvId: string) => void,
};

const highlightClass = 'highlight-blink';

const removeClass: React.MouseEventHandler<HTMLDivElement> = (e) => e.currentTarget.classList.remove(highlightClass);

export const QuestionList = (props: QuestionListProps) => {
  const {
    questionGroupId,
    toggleSelectQuestion,
    enableBulkActions,
    colour,
    startIndex,
    alternativeCode,
    preferredAltCodes,
    questionList,
    initiativeId,
    surveyId,
    highlightQuestion,
    isQuestionSelected,
    rowRef,
    isBookmarked,
    toggleBookmark,
  } = props;

  const location = useLocation();
  const history = useHistory();

  const columns = props.columns ?? defaultColumns;
  const style = colour ? { borderColor: colour } : {};

  const handleGoToQuestion = (id: string, questionIndex: number) => {
    if (props.goToQuestion) {
      return props.goToQuestion(id, questionIndex);
    }

    const surveyPath = generateUrl(ROUTES.COMPANY_TRACKER_SURVEY, {
      initiativeId,
      surveyId,
      page: 'question'
    });

    const url = `${surveyPath}/${id}/${questionIndex ?? ''}`;
    const searchParams = new URLSearchParams(location.search);

    history.push({
      pathname: url,
      search: searchParams.toString(),
    });
  }

  return (
    <LazyLoadingTrackingList
      className='questionList'
      style={style}
      forceShow={highlightQuestion?.groupId === questionGroupId}
    >
      {questionList.map((question, rowIndex) => {
        if (!question.utrv) {
          return <RowEmpty question={question} rowIndex={rowIndex} />;
        }

        const questionIndex = startIndex + rowIndex;
        const isHighlighted = highlightQuestion?.questionIndex === questionIndex;
        const utrvId = question.utrv._id;
        const columnProps: ColumnCommonProps = {
          surveyId,
          initiativeId,
          question,
          alternativeCode,
          preferredAltCodes,
          handleGoToQuestion: () => handleGoToQuestion(utrvId, questionIndex),
          isBookmarked: isBookmarked(utrvId),
          toggleBookmark: () => toggleBookmark(utrvId),
        };

        const key = `${questionGroupId}-${utrvId}-${rowIndex}`;
        const ref = highlightQuestion?.questionIndex === questionIndex ? rowRef : undefined;
        return (
          <Fragment key={key}>
            <Row
              ref={ref}
              onMouseEnter={removeClass}
              className={`clickable index-${questionIndex} ${question.utrv.status} ${
                isHighlighted ? highlightClass : ''
              }`}
            >

              {columns.map((el, ix) => React.createElement(el, { ...columnProps, key: `col-${ix}-${key}` }))}
              {enableBulkActions ? (
                <ColumnSelectbox
                  {...columnProps}
                  isSelected={isQuestionSelected(utrvId)}
                  toggleSelectQuestion={toggleSelectQuestion}
                />
              ) : null}
            </Row>
          </Fragment>
        );
      })}
    </LazyLoadingTrackingList>
  );
}
